import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Tab, Tabs, TabsProps } from "react-bootstrap"
import {
  FieldErrors,
  FieldValues,
  get,
  useFormContext,
  useWatch
} from "react-hook-form"
import { useTranslation } from "react-i18next"

import classNames from "classnames"
import { isEqual } from "lodash-es"

import { useControlledState } from "shared/lib/useControlledState"
import { IconSize } from "shared/types/Icon"
import { Icon } from "shared/ui/icon/Icon"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { RoomFormTabType } from "features/room-management/types/RoomFormTabType"

import { useRoomFormAttachmentsContext } from "../context/RoomFormAttachmentsContext"
import { useRoomFormContext } from "../contexts/RoomFormContext"
import { RoomFormAirDataTab } from "./RoomFormAirDataTab"
import { RoomFormAttachmentsTab } from "./RoomFormAttachmentsTab"
import { RoomFormClarificationRequest } from "./RoomFormClarificationRequest"
import { RoomFormGeneralTab } from "./RoomFormGeneralTab"
import { RoomFormRemarkTab } from "./RoomFormRemarkTab"
import { RoomFormRoomDataTab } from "./RoomFormRoomDataTab"

export interface RoomFormTabsProps {
  defaultTab?: RoomFormTabType
  hiddenTabs?: RoomFormTabType[]
  activeTab?: RoomFormTabType
  onTabChange?: (tab: RoomFormTabType) => void
}

const ROOM_FORM_TABS: RoomFormTabType[] = Object.values(RoomFormTabType)

const TAB_FIELD_MAPPING: Record<RoomFormTabType, string[]> = {
  [RoomFormTabType.GENERAL]: [
    "room.number",
    "room.label",
    "room.position",
    "parentID"
  ],
  [RoomFormTabType.ROOM_DATA]: [
    "room.utilization",
    "room.personsNumber",
    "room.airVolumePerArea",
    "room.airVolumePerPerson",
    "room.area",
    "room.height",
    "room.soundPressureLevel"
  ],
  [RoomFormTabType.AIR_DATA]: [
    "room.sup",
    "room.oda",
    "room.eta",
    "room.eha",
    "room.tra",
    "room.rca",
    "room.ida",
    "room.sec"
  ],
  [RoomFormTabType.REMARK]: ["room.remark"],
  [RoomFormTabType.ATTACHMENTS]: [],
  [RoomFormTabType.CLARIFICATION_REQUEST]: ["clarificationRequest.content"]
}

function useTabErrors() {
  const { subscribe, formState } = useFormContext()
  const initialErrors =
    formState.errors && Object.keys(formState.errors).length > 0
      ? formState.errors
      : null
  const [errors, setErrors] = useState<FieldErrors<FieldValues> | null>(
    initialErrors
  )
  const errorsRef = useRef(errors)

  useEffect(() => {
    return subscribe({
      formState: { errors: true },
      callback: state => {
        const next =
          state.errors && Object.keys(state.errors).length > 0
            ? { ...state.errors }
            : null
        if (!isEqual(next, errorsRef.current)) {
          errorsRef.current = next
          setErrors(next)
        }
      }
    })
  }, [subscribe])

  const tabErrors = useMemo(() => {
    const result: Record<RoomFormTabType, boolean> = {
      [RoomFormTabType.GENERAL]: false,
      [RoomFormTabType.ROOM_DATA]: false,
      [RoomFormTabType.AIR_DATA]: false,
      [RoomFormTabType.REMARK]: false,
      [RoomFormTabType.ATTACHMENTS]: false,
      [RoomFormTabType.CLARIFICATION_REQUEST]: false
    }

    Object.entries(TAB_FIELD_MAPPING).forEach(([tab, fields]) => {
      result[tab as RoomFormTabType] = fields.some(
        fieldPath => !!get(errors, fieldPath)
      )
    })

    return result
  }, [errors])

  return tabErrors
}

interface TabTitleProps {
  hasError: boolean
  children: React.ReactNode
}

const TabTitle = React.memo(({ hasError, children }: TabTitleProps) => {
  return (
    <span
      className={classNames("room-form-tab__title", {
        "nav-link__content--error": hasError,
        "room-form-tab__title--error": hasError
      })}
    >
      {hasError && (
        <Icon
          name={IconName.WARNING}
          size={IconSize.SMALL}
          className="room-form-tab__error-icon"
        />
      )}
      {children}
    </span>
  )
})

export const RoomFormTabs = React.memo((props: RoomFormTabsProps) => {
  const { t } = useTranslation()
  const {
    isReadonly,
    clarificationRequest,
    hideClarificationRequest,
    hideAttachments
  } = useRoomFormContext()
  const { linkedAttachments } = useRoomFormAttachmentsContext()
  const remark = useWatch({ name: "room.remark" })
  const { defaultTab, hiddenTabs = [], activeTab, onTabChange } = props
  const tabErrors = useTabErrors()

  const hasClarificationRequest =
    !!clarificationRequest?.data.clarificationRequest?.data ||
    clarificationRequest?.data.clarificationRequest?.isLoading

  const isTabHidden = useCallback(
    (tab: RoomFormTabType) => {
      if (tab === RoomFormTabType.REMARK) {
        return hiddenTabs.includes(tab) || (isReadonly && !remark)
      }
      if (tab === RoomFormTabType.ATTACHMENTS) {
        return (
          hiddenTabs.includes(tab) ||
          !!hideAttachments ||
          (isReadonly && !(linkedAttachments && linkedAttachments.length > 0))
        )
      }
      if (tab === RoomFormTabType.CLARIFICATION_REQUEST) {
        return (
          hiddenTabs.includes(tab) ||
          !!hideClarificationRequest ||
          (isReadonly && !hasClarificationRequest)
        )
      }
      return hiddenTabs.includes(tab)
    },
    [
      isReadonly,
      remark,
      linkedAttachments,
      hideAttachments,
      hasClarificationRequest,
      hiddenTabs,
      hideClarificationRequest
    ]
  )

  const firstAvailableTab = useMemo(() => {
    return (
      ROOM_FORM_TABS.find(tab => !isTabHidden(tab)) || RoomFormTabType.GENERAL
    )
  }, [isTabHidden])

  const [effectiveTab, handleTabChange] = useControlledState<RoomFormTabType>({
    value: activeTab,
    defaultValue: defaultTab || firstAvailableTab,
    onChange: onTabChange
  })

  const derivedActiveTab = useMemo(() => {
    if (effectiveTab && !isTabHidden(effectiveTab)) {
      return effectiveTab
    }
    if (defaultTab && !isTabHidden(defaultTab)) {
      return defaultTab
    }
    return firstAvailableTab
  }, [isTabHidden, defaultTab, effectiveTab])

  return (
    <Tabs
      activeKey={derivedActiveTab}
      onSelect={handleTabChange as TabsProps["onSelect"]}
    >
      {!isTabHidden(RoomFormTabType.GENERAL) && (
        <Tab
          key={RoomFormTabType.GENERAL}
          title={
            <TabTitle hasError={tabErrors[RoomFormTabType.GENERAL]}>
              {`${t("RoomForm.Tab.GENERAL")}*`}
            </TabTitle>
          }
          eventKey={RoomFormTabType.GENERAL}
          className={classNames({
            "room-form-tab--error": tabErrors[RoomFormTabType.GENERAL]
          })}
        >
          <RoomFormGeneralTab />
        </Tab>
      )}
      {!isTabHidden(RoomFormTabType.ROOM_DATA) && (
        <Tab
          key={RoomFormTabType.ROOM_DATA}
          title={
            <TabTitle hasError={tabErrors[RoomFormTabType.ROOM_DATA]}>
              {t("RoomForm.Tab.ROOM_DATA")}
            </TabTitle>
          }
          eventKey={RoomFormTabType.ROOM_DATA}
          className={classNames({
            "room-form-tab--error": tabErrors[RoomFormTabType.ROOM_DATA]
          })}
        >
          <RoomFormRoomDataTab />
        </Tab>
      )}
      {!isTabHidden(RoomFormTabType.AIR_DATA) && (
        <Tab
          key={RoomFormTabType.AIR_DATA}
          title={
            <TabTitle hasError={tabErrors[RoomFormTabType.AIR_DATA]}>
              {t("RoomForm.Tab.AIR_DATA")}
            </TabTitle>
          }
          eventKey={RoomFormTabType.AIR_DATA}
          className={classNames({
            "room-form-tab--error": tabErrors[RoomFormTabType.AIR_DATA]
          })}
        >
          <RoomFormAirDataTab />
        </Tab>
      )}
      {!isTabHidden(RoomFormTabType.REMARK) && (
        <Tab
          key={RoomFormTabType.REMARK}
          title={
            <TabTitle hasError={tabErrors[RoomFormTabType.REMARK]}>
              {t("RoomForm.Tab.REMARK")}
            </TabTitle>
          }
          eventKey={RoomFormTabType.REMARK}
          className={classNames({
            "room-form-tab--error": tabErrors[RoomFormTabType.REMARK]
          })}
        >
          <RoomFormRemarkTab />
        </Tab>
      )}
      {!isTabHidden(RoomFormTabType.ATTACHMENTS) && (
        <Tab
          key={RoomFormTabType.ATTACHMENTS}
          title={
            <TabTitle hasError={tabErrors[RoomFormTabType.ATTACHMENTS]}>
              {t("RoomForm.Tab.ATTACHMENTS")}
            </TabTitle>
          }
          eventKey={RoomFormTabType.ATTACHMENTS}
          className={classNames({
            "room-form-tab--error": tabErrors[RoomFormTabType.ATTACHMENTS]
          })}
        >
          <RoomFormAttachmentsTab />
        </Tab>
      )}
      {!isTabHidden(RoomFormTabType.CLARIFICATION_REQUEST) && (
        <Tab
          key={RoomFormTabType.CLARIFICATION_REQUEST}
          title={
            <span
              className={classNames("room-form-tab__title", {
                "nav-link__content--warning": hasClarificationRequest
              })}
            >
              {hasClarificationRequest && (
                <Icon
                  name={IconName.CLARIFICATION_REQUEST}
                  size={IconSize.SMALL}
                />
              )}
              {t("ClarificationRequest.Form.Tab.Label")}
            </span>
          }
          eventKey={RoomFormTabType.CLARIFICATION_REQUEST}
        >
          <div className="room-form__tab-content">
            <RoomFormClarificationRequest />
          </div>
        </Tab>
      )}
    </Tabs>
  )
})
